import { createReducer, on } from "@ngrx/store";
import { onLoadableLoad, onLoadableSuccess, onLoadableError, toLoadableWrapper, ILoadableWrapper, ILicense } from '../../models/index';
import { IFamilyUsageState, initialLicenseState } from "./license.state";
import { LicenseActions } from "./license.actions";
import { UserActions } from "../user/user.actions";
import { replaceItem } from "../common";

export const licenseReducer = createReducer(
    initialLicenseState,
    on(LicenseActions.fetchLicenses, state => ({...onLoadableLoad(state), licenses: []})),
    on(LicenseActions.fetchUsersLicenses, state => ({...onLoadableLoad(state), licenses: []})),
    on(LicenseActions.fetchLicensesSuccess, (state, {licenses}) => ({...onLoadableSuccess(state), licenses: licenses})),
    on(LicenseActions.fetchLicensesFailure, (state, {error}) => ({...onLoadableError(state, error)})),

    on(LicenseActions.loadSubscriptions, state => ({...state, subscription: {...onLoadableLoad(state.subscription), distributedLicenses: [], licenses: []} })),
    on(LicenseActions.loadSubscriptionsSuccess, (state, { subscriptionState }) => ({...state, subscription: {...onLoadableSuccess(subscriptionState)}})),
    on(LicenseActions.fetchLicensesFailure, (state, {error}) => ({...state, subscription: {...onLoadableError(state.subscription, error)}})),

    on(LicenseActions.updateSubscription, (state, {license, name, notes}) => ({
        ...state,
        subscription: {
            ...state.subscription,
            licenses: replaceItem(state.subscription.licenses, {
                ...onLoadableLoad(toLoadableWrapper(license))
            })
        }
    })),
    on(LicenseActions.updateSubscriptionSuccess, (state, { license }) => ({
        ...state,
        subscription: {
            ...state.subscription,
            licenses: replaceItem(state.subscription.licenses, {
                ...onLoadableSuccess(toLoadableWrapper(license))
            })
        }
    })),
    on(LicenseActions.updateSubscriptionFailure, (state, {license, error}) => ({
        ...state,
        subscription: {
            ...state.subscription,
            licenses: replaceItem(state.subscription.licenses, {
                ...onLoadableError(toLoadableWrapper(license), error)
            })
        }
    })),
    on(LicenseActions.revokeSubscription, (state, {license}) => ({
        ...state,
        subscription: {
            ...state.subscription,
            licenses: replaceItem(state.subscription.licenses, {
                ...onLoadableLoad(toLoadableWrapper(license))
            }),
            distributedLicenses: replaceItem(state.subscription.distributedLicenses, {
                ...onLoadableLoad(toLoadableWrapper(license))
            })
        }
    })),
    on(LicenseActions.revokeSubscriptionSuccess, (state, { licenses: revokedLicensesPayload }) => {
        const processLicenseList = (list: ILoadableWrapper<ILicense>[]) => {
            return list.map(existingWrapper => {
                const successfullyRevokedLicense = revokedLicensesPayload.find(
                    revoked => revoked.id === existingWrapper.id
                );

                if (successfullyRevokedLicense) {
                    if (!successfullyRevokedLicense.is_active) {
                        return null;
                    }
                    return {
                        ...onLoadableSuccess({
                          ...existingWrapper,
                          ...successfullyRevokedLicense
                        })
                    };
                }
                return existingWrapper;
            }).filter(item => item !== null) as ILoadableWrapper<ILicense>[];
        };

        return {
            ...state,
            subscription: {
                ...state.subscription,
                licenses: processLicenseList(state.subscription.licenses),
                distributedLicenses: processLicenseList(state.subscription.distributedLicenses)
            }
        };
    }),
    on(LicenseActions.revokeSubscriptionFailure, (state, { license, error }) => {
        const updateItemError = (item: ILoadableWrapper<ILicense>): ILoadableWrapper<ILicense> => {
            if (item.id === license.id) {
                return { ...item, ...onLoadableError(item, error) };
            }
            return item;
        };
        return {
            ...state,
            subscription: {
                ...state.subscription,
                licenses: state.subscription.licenses.map(updateItemError),
                distributedLicenses: state.subscription.distributedLicenses.map(updateItemError)
            }
        };
    }),
    on(LicenseActions.distributeLicenses, (state, {request}) => ({
        ...state,
        distributeState: {
            ...onLoadableLoad(state.distributeState)
        }
    })),
    on(LicenseActions.distributeLicensesSuccess, (state, { license }) => ({
        ...state,
        subscription: {
            ...state.subscription,
            distributedLicenses: [...state.subscription.distributedLicenses, toLoadableWrapper(license)]
        },
        distributeState: {
            ...onLoadableSuccess(state.distributeState)
        }
    })),
    on(LicenseActions.distributeLicensesFailure, (state, {error}) => ({
        ...state,
        distributeState: {
            ...onLoadableError(state.distributeState, error)
        }
    })),

    on(LicenseActions.requestLicenses, (state, {request}) => ({
        ...state,
        distributeState: {
            ...onLoadableLoad(state.distributeState)
        }
    })),
    on(LicenseActions.requestLicensesSuccess, (state, { license }) => ({
        ...state,
        subscription: {
            ...state.subscription,
            distributedLicenses: [...state.subscription.distributedLicenses, toLoadableWrapper(license)]
        },
        distributeState: {
            ...onLoadableSuccess(state.distributeState)
        }
    })),
    on(LicenseActions.requestLicensesFailure, (state, {error}) => ({
        ...state,
        distributeState: {
            ...onLoadableError(state.distributeState, error)
        }
    })),


    on(LicenseActions.fetchV2Licenses, state => ({ ...state, v2Loading: true, v2Error: null })),
    on(LicenseActions.fetchV2LicensesSuccess, (state, { licenses }) => ({ ...state, v2Licenses: licenses, v2Loading: false, v2Error: null })),
    on(LicenseActions.fetchV2LicensesFailure, (state, { error }) => ({ ...state, v2Loading: false, v2Error: error })),

    on(LicenseActions.fetchEntityLinks, state => ({ ...state, entityLinksLoading: true, entityLinksError: null, entityLinks: [] })),
    on(LicenseActions.fetchEntityLinksSuccess, (state, { links }) => ({ ...state, entityLinks: links, entityLinksLoading: false, entityLinksError: null })),
    on(LicenseActions.fetchEntityLinksFailure, (state, { error }) => ({ ...state, entityLinksLoading: false, entityLinksError: error })),

    on(LicenseActions.fetchCameraLicenses, state => ({ ...state, cameraLicensesLoading: true, cameraLicensesError: null })),
    on(LicenseActions.fetchCameraLicensesSuccess, (state, { cameraLicenses }) => ({ ...state, cameraLicenses, cameraLicensesLoading: false, cameraLicensesError: null })),
    on(LicenseActions.fetchCameraLicensesFailure, (state, { error }) => ({ ...state, cameraLicensesLoading: false, cameraLicensesError: error })),

    on(LicenseActions.fetchFamilyUsage, state => ({
        ...state,
        familyUsage: onLoadableLoad(state.familyUsage),
    })),
    on(LicenseActions.fetchFamilyUsageSuccess, (state, { family, usage }) => ({
        ...state,
        familyUsage: onLoadableSuccess<IFamilyUsageState>({
            ...state.familyUsage,
            data: { ...state.familyUsage.data, [family]: usage },
        }),
    })),
    on(LicenseActions.fetchFamilyUsageFailure, (state, { error }) => ({
        ...state,
        familyUsage: onLoadableError(state.familyUsage, error),
    })),

    on(UserActions.logout, _ => ({ ...initialLicenseState })),
)
